import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import {
  AcademicCapIcon,
  CalculatorIcon,
  BeakerIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import StandardNavbar from './StandardNavbar';
import { useAuth } from '../contexts/AuthContext';
import srsService from '../services/srsService';
import questionHistoryService from '../services/questionHistoryService';

// Proper LaTeX rendering with react-katex (same as MockTestSystem)
const renderWithLatex = (text) => {
  if (!text) return '';
  
  const parts = text.split(/(\$\$.*?\$\$|\$.*?\$)/g);
  return parts.map((part, index) => {
    if (part.startsWith('$$') && part.endsWith('$$')) {
      return <BlockMath key={index} math={part.slice(2, -2)} />;
    } else if (part.startsWith('$') && part.endsWith('$')) {
      return <InlineMath key={index} math={part.slice(1, -1)} />;
    }
    return part;
  });
};

const PracticePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [stage, setStage] = useState('choose'); // choose | running | summary
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(new Date());
  const [results, setResults] = useState([]);
  const [dataSource, setDataSource] = useState(null); // 'mhcet_question_bank_cleaned' | 'fallback'
  const [resume, setResume] = useState(null); // {subjectId, questionIndex, total}
  const [showConfig, setShowConfig] = useState(false);
  const [configStep, setConfigStep] = useState(1); // 1: mode, 2: set
  const [configMode, setConfigMode] = useState('smart'); // smart | chapter | pyq | speed
  const [configCount, setConfigCount] = useState(10); // 10 | 20 | 40
  const [configTimer, setConfigTimer] = useState(false);
  const [pendingSubject, setPendingSubject] = useState(null);
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const handleSubmitRef = useRef(null);
  const handleNextRef = useRef(null);
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('practice_resume') || 'null');
      if (saved && subjects.some(s => s.id === saved.subjectId)) {
        setResume(saved);
      }
    } catch {}
  }, []);

  // Analytics helper
  const track = (eventName, payload = {}) => {
    try {
      if (window?.gtag) {
        window.gtag('event', eventName, payload);
      }
    } catch {}
    console.log(`[analytics] ${eventName}`, payload);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (stage !== 'running' || questions.length === 0) return;
      if (!isAnswered) {
        if (e.key >= '1' && e.key <= '4') {
          const idx = parseInt(e.key, 10) - 1;
          const opt = questions[currentQuestionIndex]?.options?.[idx];
          if (opt) setSelectedAnswer(opt.id);
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (selectedAnswer && handleSubmitRef.current) handleSubmitRef.current();
        }
      } else {
        if (e.key.toLowerCase() === 'n' || e.key === 'Enter') {
          e.preventDefault();
          if (handleNextRef.current) handleNextRef.current();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage, questions, currentQuestionIndex, isAnswered, selectedAnswer]);

  // Timer effect
  useEffect(() => {
    if (!(stage === 'running' && timerEnabled && !isAnswered)) return;
    setElapsedMs(0);
    const id = setInterval(() => setElapsedMs((v) => v + 100), 100);
    return () => clearInterval(id);
  }, [stage, timerEnabled, isAnswered, currentQuestionIndex]);

  // Function to render LaTeX
  const renderLatex = (text) => {
    return renderWithLatex(text);
  };


  const subjects = [
    {
      id: 'maths',
      name: 'Mathematics',
      icon: CalculatorIcon,
      description: 'Algebra, Calculus, Geometry, Trigonometry',
      color: 'bg-indigo-600'
    },
    {
      id: 'physics',
      name: 'Physics',
      icon: CpuChipIcon,
      description: 'Mechanics, Electricity, Optics, Thermodynamics',
      color: 'bg-emerald-600'
    },
    {
      id: 'chemistry',
      name: 'Chemistry',
      icon: BeakerIcon,
      description: 'Organic, Inorganic, Physical Chemistry',
      color: 'bg-rose-600'
    }
  ];

  const fetchPracticeQuestions = async (subjectId) => {
    try {
      // Fetch strictly from mhcet_question_bank_cleaned
      const ref = collection(db, 'mhcet_question_bank_cleaned');
      // Simple query with exact subject field value
      console.log(`[Practice] Querying for subject: "${subjectId}"`);
      const q = query(ref, where('subject', '==', subjectId));
      const snap = await getDocs(q);
      console.log(`[Practice] Found ${snap.size} questions for ${subjectId}`);
      // If no questions found, let's debug what's in the collection
      if (snap.size === 0) {
        console.log('[Practice] No questions found, checking collection...');
        const sampleQuery = query(ref, limit(3));
        const sampleSnap = await getDocs(sampleQuery);
        if (!sampleSnap.empty) {
          console.log('[Practice] Sample documents:');
          sampleSnap.docs.forEach((doc, idx) => {
            const data = doc.data();
            console.log(`[Practice] Doc ${idx + 1}:`, data);
            console.log(`[Practice] Subject field value: "${data.subject}"`);
          });
        } else {
          console.log('[Practice] Collection appears to be empty');
        }
      }
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Transform to uniform shape for MVP
      const transformed = fetched.map((q, idx) => {
        const text = q.questionText || q.question || q.text || q.Question || `Question ${idx + 1}`;
        let optionsArr = [];
        if (Array.isArray(q.options)) {
          optionsArr = q.options.map((opt, i) => ({ id: ['A','B','C','D'][i] || String(i+1), text: opt.text || opt || '', correct: !!opt.correct }));
        } else if (q.options && typeof q.options === 'object') {
          optionsArr = Object.entries(q.options).map(([k, v]) => ({ id: k.toUpperCase(), text: typeof v === 'string' ? v : (v?.text || ''), correct: v?.correct || false }));
        } else if (q.option_a || q.optionA || q.A) {
          optionsArr = ['A','B','C','D'].map(k => ({ id: k, text: q[`option_${k.toLowerCase()}`] || q[`option${k}`] || q[k] || '', correct: false }));
        } else if (q.a || q.b || q.c || q.d) {
          optionsArr = ['A','B','C','D'].map(k => ({ id: k, text: q[k.toLowerCase()] || q[k] || '', correct: (q.answer || '').toString().toUpperCase().includes(k) }));
        }
        if (!optionsArr.some(o => o.correct) && (q.correctAnswer || q.correct_option || q.correctOption || q.answer)) {
          const correctKey = (q.correctAnswer || q.correct_option || q.correctOption || q.answer).toString().toUpperCase();
          optionsArr = optionsArr.map(o => ({ ...o, correct: o.id.toString().toUpperCase() === correctKey }));
        }
        optionsArr = optionsArr.filter(o => o.text).slice(0, 4);
        const result = {
          questionId: q.id || q.questionId || `q_${idx}`,
          questionText: text,
          options: optionsArr,
          subject: q.subject || subjectId,
          topic: q.topic || q.chapter || q.subtopic || null,
          subskill: q.subskill || q.concept || null,
          source: q.source || (q.isPYQ ? 'pyq' : 'bank'),
          difficulty: q.difficulty || 'medium',
          hint: q.hint || null,
          explanation: q.explanation || q.solution || null
        };
        return result;
      }).filter(t => t.options && t.options.length >= 2);
      const shuffled = transformed.sort(() => Math.random() - 0.5).slice(0, 10);
      if (shuffled.length > 0) {
        setDataSource('mhcet_question_bank_cleaned');
      }
      return shuffled;
    } catch (e) {
      console.error('Practice fetch error:', e);
      return [];
    }
  };

  const openConfig = (subject, presetMode) => {
    setPendingSubject(subject);
    if (presetMode) setConfigMode(presetMode);
    setConfigStep(1);
    setShowConfig(true);
    track('practice_config_start', { subject: subject?.id, presetMode: presetMode || null });
  };

  const startPracticeWithConfig = async () => {
    if (!pendingSubject) return;
    setSelectedSubject(pendingSubject);
    const qs = await fetchPracticeQuestions(pendingSubject.id);
    let finalQs = qs;
    if (Array.isArray(qs) && qs.length > 0) {
      finalQs = qs.slice(0, configCount);
    }
    if (!finalQs || finalQs.length === 0) {
      finalQs = [{
        questionId: 'fallback_1',
        questionText: '2 + 2 = ?',
        options: [
          { id: 'A', text: '3', correct: false },
          { id: 'B', text: '4', correct: true },
          { id: 'C', text: '5', correct: false },
          { id: 'D', text: '22', correct: false }
        ],
        subject: pendingSubject.id,
        topic: null,
        subskill: null,
        source: 'fallback',
        difficulty: 'easy'
      }];
      setDataSource('fallback');
    } else {
      if (!dataSource) setDataSource('mhcet_question_bank_cleaned');
    }
    setQuestions(finalQs);
    setStage('running');
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setResults([]);
    setQuestionStartTime(new Date());
    setTimerEnabled(!!configTimer);
    setShowHint(false);
    setHintUsed(false);
    track('practice_config_done', { subject: pendingSubject.id, mode: configMode, count: configCount, timer: !!configTimer });
    track('practice_view', { subject: pendingSubject.id, mode: configMode });
    try {
      localStorage.setItem('practice_resume', JSON.stringify({ subjectId: pendingSubject.id, questionIndex: 0, total: finalQs.length }));
      setResume({ subjectId: pendingSubject.id, questionIndex: 0, total: finalQs.length });
    } catch {}
    setShowConfig(false);
  };

  const handleSubjectSelect = (subject) => openConfig(subject);





  if (stage === 'running' && questions.length > 0) {
    const currentQuestion = questions[currentQuestionIndex];
    const handleAnswerSelect = (id) => {
      if (!isAnswered) setSelectedAnswer(id);
    };
    const handleSubmit = () => {
      if (!selectedAnswer) return;
      const end = new Date();
      const responseTime = (end - questionStartTime) / 1000;
      const correctOption = currentQuestion.options.find(o => o.correct);
      const correct = selectedAnswer === (correctOption?.id || '');
      setIsCorrect(correct);
      setIsAnswered(true);
      const resultEntry = { questionId: currentQuestion.questionId, isCorrect: correct, responseTime, selectedAnswer, hintUsed };
      setResults(prev => ([...prev, resultEntry]));
      try { track('question_submit', { subject: selectedSubject?.id, questionId: currentQuestion.questionId, correct, responseTime_s: responseTime, hintUsed }); } catch {}
      const latencyMs = Math.round(responseTime * 1000);
      if (user?.uid) {
        try { questionHistoryService.recordQuestionAsked(user.uid, currentQuestion.questionId, { isCorrect: correct, responseTime }); } catch (e) { console.error('recordQuestionAsked error', e); }
        try {
          const conceptId = currentQuestion.subskill || currentQuestion.topic || `${selectedSubject?.id}_generic`;
          const difficulty = currentQuestion.difficulty || srsService.getConceptDifficulty(conceptId);
          const review = srsService.sm2plus({ correct, latencyMs, difficulty });
          srsService.updateMastery(user.uid, conceptId, review, latencyMs, correct);
        } catch (e) { console.error('SRS update error', e); }
      }
    };
    const handleNext = () => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(i => i + 1);
        setSelectedAnswer(null);
        setIsAnswered(false);
        setQuestionStartTime(new Date());
        setShowHint(false);
        setHintUsed(false);
        try { localStorage.setItem('practice_resume', JSON.stringify({ subjectId: selectedSubject?.id, questionIndex: currentQuestionIndex + 1, total: questions.length })); } catch {}
      } else {
        try { localStorage.removeItem('practice_resume'); } catch {}
        try { track('practice_complete', { subject: selectedSubject?.id, total: questions.length, correct: results.filter(r => r.isCorrect).length }); } catch {}
        if (user?.uid) {
          try {
            const wrong = results.filter(r => !r.isCorrect);
            let added = 0;
            wrong.forEach(r => {
              const q = questions.find(q => q.questionId === r.questionId) || {};
              const conceptId = q.subskill || q.topic || `${selectedSubject?.id}_generic`;
              const review = srsService.sm2plus({ correct: false, latencyMs: 5000, difficulty: q.difficulty || 'medium' });
              srsService.updateMastery(user.uid, conceptId, review, 5000, false);
              added += 1;
            });
            if (added > 0) track('add_to_srs_count', { count: added });
          } catch (e) { console.error('SRS bulk add error', e); }
        }
        setStage('summary');
      }
    };
    handleSubmitRef.current = handleSubmit;
    handleNextRef.current = handleNext;

    return (
      <div className="min-h-screen bg-gray-50">
        <StandardNavbar title={`${selectedSubject?.name} Practice`} showBack={true} showHome={true} />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-700">Q{currentQuestionIndex + 1}/{questions.length}</div>
            <div className="flex items-center gap-2">
              {timerEnabled && (
                <div className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm">{Math.floor(elapsedMs/1000)}s</div>
              )}
              <button onClick={() => setTimerEnabled(v=>!v)} className="text-sm px-3 py-1 border rounded-md hover:bg-gray-50">{timerEnabled ? 'Timer: On' : 'Timer: Off'}</button>
              <button onClick={() => { try { localStorage.setItem('practice_resume', JSON.stringify({ subjectId: selectedSubject?.id, questionIndex: currentQuestionIndex, total: questions.length })); } catch {} navigate('/dashboard'); }} className="text-sm px-3 py-1 border rounded-md hover:bg-gray-50">Pause</button>
              <button onClick={() => { const reason = window.prompt('Report issue (Ambiguous / Typo / Wrong key):', 'Ambiguous'); if (reason) { track('question_flag', { questionId: currentQuestion.questionId, reason }); alert('Thanks! We will review this question.'); } }} className="text-sm px-3 py-1 border rounded-md hover:bg-gray-50">Report</button>
              <button onClick={() => setShowExitConfirm(true)} className="text-sm px-3 py-1 border rounded-md hover:bg-gray-50">Exit</button>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedSubject?.name} Practice</h2>
              <p className="text-gray-600">Question {currentQuestionIndex + 1} of {questions.length}</p>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {renderLatex(currentQuestion.questionText)}
            </h3>
            {!isAnswered && (
              <div className="mb-4">
                <button onClick={() => { setShowHint(true); setHintUsed(true); }} className="text-sm text-blue-600 hover:underline">Show hint</button>
                {showHint && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-md text-sm">{currentQuestion.hint || 'Hint: Re-check units and signs.'}</div>
                )}
              </div>
            )}
            <div className="space-y-3 mb-6">
              {currentQuestion.options.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => handleAnswerSelect(opt.id)}
                  disabled={isAnswered}
                  className={`w-full p-4 text-left border-2 rounded-lg transition-all ${
                    selectedAnswer === opt.id
                      ? isAnswered
                        ? opt.correct ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
                        : 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <span className="font-medium mr-3">{opt.id})</span>
                  <span>{renderLatex(opt.text)}</span>
                </button>
              ))}
            </div>
            {isAnswered && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-900 rounded-md text-sm">
                Why this is {isCorrect ? 'correct' : 'incorrect'}: {currentQuestion.explanation || 'Focus on the governing formula and typical traps for this concept.'}
                <button onClick={handleNext} className="ml-3 text-green-700 underline">See a similar one</button>
              </div>
            )}
            {!isAnswered ? (
              <button onClick={handleSubmit} disabled={!selectedAnswer} className="px-6 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50">Submit</button>
            ) : (
              <button onClick={handleNext} className="px-6 py-3 bg-green-600 text-white rounded-lg">{currentQuestionIndex < questions.length - 1 ? 'Next' : 'Finish'}</button>
            )}
            {!isAnswered && (
              <div className="fixed left-0 right-0 bottom-0 md:hidden p-3 bg-white border-t shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
                <button onClick={handleSubmit} disabled={!selectedAnswer} className="w-full py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50">Submit</button>
              </div>
            )}
          </div>
        </div>
        {showExitConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full">
              <h4 className="font-semibold text-gray-900 mb-2">Save and leave?</h4>
              <p className="text-sm text-gray-600 mb-4">Your progress will be saved so you can resume later.</p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowExitConfirm(false)} className="px-4 py-2 rounded-md border">Cancel</button>
                <button onClick={() => { try { localStorage.setItem('practice_resume', JSON.stringify({ subjectId: selectedSubject?.id, questionIndex: currentQuestionIndex, total: questions.length })); } catch {} navigate('/dashboard'); }} className="px-4 py-2 rounded-md bg-blue-600 text-white">Save & Exit</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

     if (stage === 'summary') {
     const correct = results.filter(r => r.isCorrect).length;
     const accuracy = Math.round((correct / results.length) * 100);
     const avgTime = Math.round(results.reduce((s, r) => s + r.responseTime, 0) / results.length);
     return (
       <div className="min-h-screen bg-gray-50">
         <StandardNavbar title="Practice Summary" showBack={true} showHome={true} />
         <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Practice Summary</h2>
            <p className="text-gray-700">Accuracy: <span className="font-semibold">{accuracy}%</span></p>
            <p className="text-gray-700">Average Time: <span className="font-semibold">{avgTime}s</span></p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <input type="checkbox" checked readOnly className="h-4 w-4" />
              <span className="text-sm text-gray-700">Add all wrong to Reviews (SRS)</span>
            </div>
            <div className="mt-6 flex gap-3 justify-center">
              <button onClick={() => setStage('choose')} className="px-6 py-3 bg-gray-100 text-gray-800 rounded-lg">Practice More</button>
              <button onClick={() => navigate('/dashboard')} className="px-6 py-3 bg-blue-600 text-white rounded-lg">Go to Dashboard</button>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button onClick={() => openConfig(selectedSubject, 'smart')} className="px-4 py-2 rounded-md border">Fix weak area (5Q)</button>
              <button onClick={() => openConfig(selectedSubject, 'smart')} className="px-4 py-2 rounded-md border">Quick recap (1 min)</button>
              <button onClick={() => openConfig(selectedSubject, 'smart')} className="px-4 py-2 rounded-md border">Mini-Mock 10Q Mixed PCM</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <StandardNavbar title="Practice Questions" showBack={true} showHome={true} />
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose a subject to start practicing with our adaptive learning system. 
            Questions will adjust to your performance level.
          </p>
        </div>

        {/* Resume last set pill */}
        {resume && (
          <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm">Resume last set: <span className="font-semibold">{subjects.find(s=>s.id===resume.subjectId)?.name}</span> • Q{(resume.questionIndex||0)+1}/{resume.total}</div>
            <button onClick={() => openConfig(subjects.find(s => s.id === resume.subjectId))} className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Resume</button>
          </div>
        )}

        {/* Quick Start row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <button onClick={() => openConfig(subjects[0], 'smart')} className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md">
            <div className="font-semibold text-gray-900">10Q Smart Practice</div>
            <div className="text-sm text-gray-600">Adaptive by weak sub-skills</div>
          </button>
          <button onClick={() => openConfig(subjects[0], 'pyq')} className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md">
            <div className="font-semibold text-gray-900">PYQs (2013–2024)</div>
            <div className="text-sm text-gray-600">Filter by year/chapter</div>
          </button>
          <button onClick={() => openConfig(subjects[0], 'chapter')} className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md">
            <div className="font-semibold text-gray-900">Chapter Drill</div>
            <div className="text-sm text-gray-600">Pick topic • 10/20Q</div>
          </button>
          <button onClick={() => openConfig(subjects[0], 'speed')} className="bg-white rounded-lg shadow p-4 text-left hover:shadow-md">
            <div className="font-semibold text-gray-900">Speed Drill (timed)</div>
            <div className="text-sm text-gray-600">1 min/question</div>
          </button>
        </div>

        {/* Subject Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {subjects.map((subject) => (
            <div
              key={subject.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer flex flex-col h-full"
              onClick={() => handleSubjectSelect(subject)}
            >
              {/* Subject Icon */}
              <div className={`${subject.color} p-6 rounded-t-xl`}>
                <div className="flex items-center justify-between">
                  <subject.icon className="h-12 w-12 text-white" />
                </div>
              </div>

              {/* Subject Info */}
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {subject.name}
                </h3>
                <p className="text-gray-600 mb-4">
                  {subject.description}
                </p>

                {/* Duration chips */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    {t:'5m · 10Q'},
                    {t:'12m · 20Q'},
                    {t:'25m · 40Q'}
                  ].map((c,i)=> (
                    <span key={i} className="px-2 py-1 text-xs bg-gray-100 rounded-full text-gray-700">{c.t}</span>
                  ))}
                </div>

                {/* Recommended topic (placeholder) */}
                <div className="text-sm text-gray-700 mb-4">
                  Next: <span className="font-medium">
                    {subject.id==='maths' && 'Quadratic Equations – Factorization'}
                    {subject.id==='physics' && 'Kinematics – Projectile'}
                    {subject.id==='chemistry' && 'Chemical Thermodynamics'}
                  </span> <span className="text-orange-600">(weak)</span>
                </div>

                {/* Start Button */}
                <button onClick={() => openConfig(subject)} className="mt-auto w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                  Start Practice
                </button>
              </div>
            </div>
          ))}
        </div>

         {/* Features Section */}
         <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
           <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
             Adaptive Learning Features
           </h2>
           
           <div className="grid md:grid-cols-3 gap-8">
             <div className="text-center">
               <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                 <AcademicCapIcon className="h-8 w-8 text-blue-600" />
               </div>
               <h3 className="font-semibold text-gray-900 mb-2">Smart Difficulty</h3>
               <p className="text-gray-600">
                 Questions automatically adjust based on your performance and accuracy
               </p>
             </div>
             
             <div className="text-center">
               <div className="bg-green-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                 <CalculatorIcon className="h-8 w-8 text-green-600" />
               </div>
               <h3 className="font-semibold text-gray-900 mb-2">Real CET Questions</h3>
               <p className="text-gray-600">
                 Practice with authentic previous year MH-CET questions
               </p>
             </div>
             
                          <div className="text-center">
                <div className="bg-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <CpuChipIcon className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Performance Tracking</h3>
                <p className="text-gray-600">
                  Track your progress, streaks, and identify weak areas
                </p>
              </div>
           </div>
         </div>
      </div>
      {/* Pre-session 2-step config modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Configure Practice</h3>
            {configStep === 1 ? (
              <div>
                <label className="block text-sm text-gray-700 mb-2">Mode</label>
                <div className="grid grid-cols-2 gap-2">
                  {['smart','chapter','pyq','speed'].map(m => (
                    <button key={m} onClick={() => setConfigMode(m)} className={`px-3 py-2 rounded-md border ${configMode===m?'border-blue-600 text-blue-700 bg-blue-50':'hover:bg-gray-50'}`}>{m.charAt(0).toUpperCase()+m.slice(1)}</button>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <button onClick={() => setConfigStep(2)} className="px-4 py-2 rounded-md bg-blue-600 text-white">Next</button>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm text-gray-700 mb-2">Questions</label>
                <div className="flex gap-2 mb-4">
                  {[10,20,40].map(n => (
                    <button key={n} onClick={() => setConfigCount(n)} className={`px-3 py-2 rounded-md border ${configCount===n?'border-blue-600 text-blue-700 bg-blue-50':'hover:bg-gray-50'}`}>{n}</button>
                  ))}
                </div>
                <label className="block text-sm text-gray-700 mb-2">Timer</label>
                <div className="flex items-center gap-2 mb-4">
                  <button onClick={() => setConfigTimer(v=>!v)} className={`px-3 py-2 rounded-md border ${configTimer?'border-blue-600 text-blue-700 bg-blue-50':'hover:bg-gray-50'}`}>{configTimer ? 'On' : 'Off'}</button>
                  <span className="text-sm text-gray-600">1 min/question (recommend)</span>
                </div>
                <div className="mt-2 flex justify-between">
                  <button onClick={() => setConfigStep(1)} className="px-4 py-2 rounded-md border">Back</button>
                  <div className="flex gap-2">
                    <button onClick={() => setShowConfig(false)} className="px-4 py-2 rounded-md border">Cancel</button>
                    <button onClick={startPracticeWithConfig} className="px-4 py-2 rounded-md bg-blue-600 text-white">Start</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticePage;
